import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Table } from 'react-bootstrap';
import { useNavigate, useLocation } from "react-router-dom";
import { showToast } from '../config/Alert';
import child from "../assets/images/child.png";
import { getFacilityPlans, renewFacilityPlan, subscribeFacilityPlan } from '../services/FacilityApi';
import { applyCoupon } from '../services/CouponApi';
import LoadingSpinner from './LoadingSpinner';
import config from '../config/config';

const FacilityPlans = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const [plans, setPlans] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const statusKey = location.state?.statusKey;
  const subscriptionId = location.state?.subscriptionId;

  useEffect(() => {
    fetchFacilityPlans();
  }, []);

  const fetchFacilityPlans = async () => {
    setLoading(true);
    try {
      const response = await getFacilityPlans();
      setPlans(response.data || []);
    } catch (error) {
      showToast("Error fetching plans", 'error');
    }
    setLoading(false);
  };

  const handlePlanChange = (event) => {
    const planId = event.target.value;
    const plan = plans.find(p => p.id === parseInt(planId));
    setSelectedPlan(plan);
  };

  const handleApplyCoupon = async () => {
    try {
      const response = await applyCoupon({ coupon_code: couponCode });
      const data = await response.data.coupon;
      // Check if it's for the cart (programs)
      if (data.coupon_for === "Facility") {
        setCouponData(data);
        setCouponError("");
      } else {
        setCouponData(null);
        setCouponError("This coupon is not valid for facility subscription");
      }
    } catch (error) {
      setCouponData(null);
      setCouponError(error.message);
    }
  };

  const calculateFacilityAmount = () => {
    if (!selectedPlan) return null;

    const finalAmount = parseFloat(selectedPlan.final_amount);
    const kitAmount = parseFloat(selectedPlan.inital_kit_amount);
    let discount = 0;

    if (couponData?.coupon_for === "Facility") {
      if (couponData.coupon_type === "percent") {
        discount = finalAmount * (parseFloat(couponData.value) / 100);
      } else if (couponData.coupon_type === "amount") {
        discount = parseFloat(couponData.value);
      }
      if (discount > finalAmount) discount = finalAmount;
    }

    // Initial Kit should only be added if it's a new payment (not renewal)
    const totalToPay = finalAmount - discount + (statusKey === 'nopayment' ? kitAmount : 0);

    return {
      finalAmount, kitAmount: statusKey === 'nopayment' ? kitAmount : 0, discount, totalToPay
    };
  };

  const amounts = calculateFacilityAmount();

  const handleRenew = async (transactionId) => {
    const formData = new FormData();
    formData.append("payment_plan_id", selectedPlan.id);
    formData.append("transaction_id", transactionId);
    formData.append("amount_paid", amounts.totalToPay);
    if (couponData?.id) formData.append("coupon_id", couponData.id);

    try {
      await renewFacilityPlan(formData, subscriptionId);
      showToast("Subscription Renew Successfull!", 'success');
      navigate("/viewChildProfile");
    } catch (error) {
      showToast("Failed to subscribe", 'error');
    }
  }

  const handleSubscription = async (transactionId) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("user_id", user.id);
    formData.append("child_id", localStorage.getItem("ChildID"));
    formData.append("amount_paid", amounts.totalToPay);
    formData.append("payment_plan_id", selectedPlan.id);
    formData.append("transaction_id", transactionId);
    if (couponData?.id) formData.append("coupon_id", couponData.id);

    try {
      await subscribeFacilityPlan(formData);
      showToast("Subscription Successfull!", 'success');
      localStorage.removeItem("ChildID");
      navigate("/viewChildProfile");
    } catch (error) {
      showToast("Failed to subscribe", 'error');
    }
    setLoading(false);
  };

  const handlePayment = () => {
    const amounts = calculateFacilityAmount();
    if (!amounts) return;

    const options = {
      key: config.Razorpay_Key,
      amount: amounts.totalToPay * 100, // Amount in paise
      currency: 'INR',
      name: 'The Pied Piper',
      description: 'Facility Plan Payment',
      handler: async function (response) {
        // alert(`Payment successful. Payment ID: ${response.razorpay_payment_id}`);
        // Handle post-payment actions here
        if (statusKey === 'nopayment') {
          await handleSubscription(response.razorpay_payment_id);
        } else {
          await handleRenew(response.razorpay_payment_id);
        }
      },
      prefill: {
        name: user.name,
        email: user.email,
       // contact: user.contact,
      },
      notes: {
        plan_id: selectedPlan.id,
      },
      theme: {
        color: '#3399cc',
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className='app-container mobile-bottom'>
      <Container className='mb-3'>
        <Form>
          <div className='AccountInnerMain d-flex align-items-center justify-content-between'>
            <h6>
              <img src={child} width={20} alt='user' />
              <span className='ps-1'>Plan Subscription</span>
            </h6>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Select Plan</Form.Label>
            <Form.Select onChange={handlePlanChange}>
              <option value="">Select a plan</option>
              {plans.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.duration_months} Month(s)
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Apply Coupon Code */}
          <div className='border-top pt-3 mb-3'>
            <p className='mb-0 fs-12 fw-5'>Promo Code / Coupon</p>
            <div className='d-flex gap-2'>
              <Form.Group controlId="exampleForm.ControlInput1" >
                <Form.Control disabled={!selectedPlan}
                  size='sm' type="text" placeholder="Enter here" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} />
              </Form.Group>
              <Form.Group controlId="exampleForm.ControlInput1">
                <Button variant='danger' size='small' type='button' className='fw-7 fs-12' onClick={handleApplyCoupon} disabled={!selectedPlan}>Apply</Button>
              </Form.Group>
            </div>
            {couponData && <small className='text-success fw-5 fs-10 d-block'>Coupon code applied successfully</small>}
            {couponError && <small className='text-danger fw-5 fs-10 d-block'>{couponError}</small>}
          </div>

          {selectedPlan && amounts && (
            <div className="mb-3">
              <Table>
                <tbody>
                  <tr><td>Subscription Amount: </td><th>₹ {amounts.finalAmount}</th></tr>
                  {couponData && <tr><td className='text-success'>Discount Applied:</td><th className='text-success'>
                    {couponData.coupon_type === "percent" ? `- ₹ ${amounts.discount} (${couponData.value}%)` : `- ₹ ${couponData.value}`}</th></tr>}
                  {statusKey === 'nopayment' && <tr><td>Initial Kit Amount:</td><th>₹ {amounts.kitAmount}</th></tr>}
                  <tr><td>To Pay:</td><th>₹ {amounts.totalToPay.toFixed(2)}</th></tr>
                </tbody>
              </Table>
            </div>
          )}

          <Form.Group className="d-flex justify-content-between">
            <Button variant="danger" size='sm' type="button" className='text-white px-3 rounded-5' onClick={handlePayment} disabled={!selectedPlan}>
              Pay Now
            </Button>
          </Form.Group>
        </Form>
      </Container>
    </div>
  );
};

export default FacilityPlans;